import { z } from 'zod';
import { REQUIRED_APPROVALS_MAX, REQUIRED_APPROVALS_MIN } from './board';
import { publicProfileSchema } from './user';

/**
 * PR-list card approvals — owner-directed, no requirement.
 *
 * A card in the board's PR list (`lists.is_pr_list`) collects one verdict per
 * member: `approved` or `rejected`, with an optional reason that is NOT a
 * comment. `met` means the card is in the PR list and its approved count has
 * reached `boards.required_approvals`; rejections are counted and shown but do
 * not block it.
 */
export const APPROVAL_REASON_MAX_LENGTH = 2000;

export const approvalVerdictSchema = z.enum(['approved', 'rejected']);
export type ApprovalVerdict = z.infer<typeof approvalVerdictSchema>;

/**
 * The body of `PUT /cards/:id/approval`. The reason is trimmed, and an empty
 * (or whitespace-only) one collapses to `null`, so no row holds `''`.
 */
export const cardApprovalPutSchema = z.strictObject({
  verdict: approvalVerdictSchema,
  reason: z
    .string()
    .nullish()
    .transform((value) => {
      const trimmed = value?.trim() ?? '';
      return trimmed === '' ? null : trimmed;
    })
    .pipe(z.string().max(APPROVAL_REASON_MAX_LENGTH).nullable()),
});
export type CardApprovalPut = z.infer<typeof cardApprovalPutSchema>;

/** One member's verdict on the card back. */
export const cardApprovalEntrySchema = z.object({
  user: publicProfileSchema,
  verdict: approvalVerdictSchema,
  reason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CardApprovalEntry = z.infer<typeof cardApprovalEntrySchema>;

/**
 * `cardDetailSchema.approvals` — visible to anyone who can read the card
 * (comments' footing). `entries` are ordered by `updatedAt` descending.
 */
export const cardApprovalsSchema = z.object({
  inPrList: z.boolean(),
  requiredApprovals: z
    .number()
    .int()
    .min(REQUIRED_APPROVALS_MIN)
    .max(REQUIRED_APPROVALS_MAX),
  approvedCount: z.number().int().nonnegative(),
  rejectedCount: z.number().int().nonnegative(),
  met: z.boolean(),
  entries: z.array(cardApprovalEntrySchema),
});
export type CardApprovals = z.infer<typeof cardApprovalsSchema>;

/**
 * `boardFaceCardSchema.approvals` — the card-front summary. `null` unless the
 * card sits in the board's PR list.
 */
export const cardFrontApprovalsSchema = z.object({
  approved: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  required: z.number().int().min(REQUIRED_APPROVALS_MIN).max(REQUIRED_APPROVALS_MAX),
  met: z.boolean(),
});
export type CardFrontApprovals = z.infer<typeof cardFrontApprovalsSchema>;
